import type { VoidFunctionComponent } from 'react';

import { isLinuxFetcher } from '../../../../../../shared/fetchers';
import Locator from '../../../../../../shared/Locator';
import { Exploitability } from '../../../../../../shared/vulns/types';
import VulnBadge from '../../../../generic/VulnBadge';
import CVEBadge from '../../../../issues/IssuesInbox/IssueItem/CVEBadge';
import EpssBadge from '../../../../issues/IssuesInbox/IssueItem/EpssBadge';
import FixBadge from '../../../../issues/IssuesInbox/IssueItem/FixBadge';
import KnownExploitBadge from '../../../../issues/IssuesInbox/IssueItem/KnownExploitBadge';
import Header from '../../../../shared/vulns/Drawer/Header';
import type { SelectedVuln } from '../../../../../../shared/organizationPackages/response';

import {
  getExploitability,
  getSelectedVulnCve,
  getSelectedVulnCvss,
  getSelectedVulnEpss,
  getSelectedVulnRemediation,
  getSelectedVulnTitle,
} from './utils';

type Props = {
  isLoading: boolean;
  packageRevisionLocator: string;
  selectedVuln?: SelectedVuln;
};

/**
 * Container component for the VulnerabilityDrawerHeader
 */
const PackageVersionVulnerabilityDrawerHeader: VoidFunctionComponent<Props> = ({
  isLoading,
  packageRevisionLocator,
  selectedVuln,
}) => {
  const title = getSelectedVulnTitle(selectedVuln);
  const cvss = getSelectedVulnCvss(selectedVuln);
  const cve = getSelectedVulnCve(selectedVuln);
  const epss = getSelectedVulnEpss(selectedVuln);
  const exploitability = getExploitability(selectedVuln);
  const locatorParts = Locator.parse(packageRevisionLocator);
  const remediation = getSelectedVulnRemediation(selectedVuln);
  const isLinuxDep = isLinuxFetcher(locatorParts?.fetcher);
  const shouldShowFixBadge = remediation && !isLinuxDep;

  return (
    <Header
      isLoading={isLoading}
      title={title}
      locatorParts={locatorParts}
      badgesSlot={
        <>
          <VulnBadge cvss={cvss} isTruncated hasTooltip={false} />
          {epss && <EpssBadge tooltipId={`epss-badge-${cve}`} epss={epss} showTooltip={false} />}
          {shouldShowFixBadge && (
            <FixBadge
              version={remediation.partialFix}
              distance={remediation.partialFixDistance}
              showTooltip={false}
            />
          )}
          {cve && <CVEBadge cve={cve} showTooltip={false} /> }
          {exploitability !== Exploitability.Unknown && <KnownExploitBadge showTooltip={false} />}
        </>
      }
    />
  );
};

export default PackageVersionVulnerabilityDrawerHeader;
